import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./css/Header.css";   // ✅ use Header.css explicitly
import { usePermissions, RequirePermission } from "./hooks/usePermissions";


const Header = ({ showButtons, isAdmin, userData }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [userCredits, setUserCredits] = useState(null);
  const [showRequestCreditsModal, setShowRequestCreditsModal] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState(100);
  const [requestError, setRequestError] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);


  useEffect(() => {
    if (!userData || !userData.authenticated) return;

    async function fetchCredits() {
      try {
        const res = await fetch("/api/user/credits", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`
          }
        });

        const data = await res.json();
        if (data && typeof data.balance === "number") {
          setUserCredits(data.balance);
        }
      } catch (err) {
        console.error("Failed to load user credits:", err);
      }
    }

    fetchCredits();
  }, [userData]);

  // Close the profile dropdown when clicking outside it. Declared before any
  // early return so this hook always runs in the same order.
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Check if user is authenticated
  //if (!userData || Object.keys(userData).length === 0) return null;
  if (!userData || !userData.authenticated) {
  return (
    <header className="app-header">
      <div className="header-inner">
        <div className="logo">
          <img src="/cloudgenomics_logo.png" alt="CloudGenomics" />
        </div>
      </div>
    </header>
  );
}

  
  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include"
      });

      window.location.href =
        "https://us-east-15omn7uhsf.auth.us-east-1.amazoncognito.com/logout" +
        "?client_id=31lq7qsuf5epcgbh7kocl350sa" +
        "&logout_uri=https://cloudgenomics.net/login";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  async function submitCreditRequest() {
    try {
      const res = await fetch("/api/user/credits/request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`
        },
        body: JSON.stringify({ amount: requestedAmount })
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Failed to submit credit request.");
        return;
      }

      alert("Your credit request has been submitted.");
      setShowRequestCreditsModal(false);
    } catch (err) {
      console.error("Error submitting credit request:", err);
      alert("Failed to submit credit request.");
    }
  }

  // (click-outside handler is registered above, before the early return, to
  // satisfy the rules-of-hooks — hooks must run in the same order every render.)

  return (
    <header className="app-header">
      <div className="header-inner">
        {/* Logo — extreme left */}
        <div className="logo">
          <img src="/cloudgenomics_logo.png" alt="CloudGenomics" />
        </div>

        {showButtons && (
          <nav className="nav">
            {isAdmin && <Link to="/admin" className="nav-link">Admin</Link>}
            <Link to="/" className="nav-link">Home</Link>

            <RequirePermission permission="viewBuilds">
              <Link to="/all-chats" className="nav-link">Your Builds</Link>
            </RequirePermission>

            <RequirePermission permission="viewData">
            <Link to="/user-data" className="nav-link">Your Data</Link>
            </RequirePermission>

            <RequirePermission permission="viewAnalyses">
            <button className="nav-link" onClick={() => navigate("/analyses")}>
              Analyses
            </button>
            </RequirePermission>

            <Link to="/about" className="nav-link">About</Link>

            <div className="user-menu">
              <button
                className="user-button"
                ref={buttonRef}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {userData.name || "User"} ▾
              </button>

              {dropdownOpen && (
                <div ref={dropdownRef} className="user-dropdown">
                  <p><strong>Name:</strong> {userData.name || "Unknown"}</p>
                  <p>
                    <strong>Groups:</strong>{" "}
                    {userData.userGroups?.length
                      ? userData.userGroups
                          .map(g => g.charAt(0).toUpperCase() + g.slice(1))
                          .join(", ")
                      : "None"}
                  </p>
                  <p><strong>Organization:</strong> {userData.organization || "N/A"}</p>
                  <p><strong>Available Credits:</strong> {userCredits ?? "—"}</p>
                  <button
                    className="request-credits-button"
                    onClick={() => setShowRequestCreditsModal(true)}
                    style={{ marginTop: "10px" }}
                  >
                    Request Credits
                  </button>

                  <button
                    className="logout-button"
                    onClick={(e) => {
                      e.preventDefault();   // ⬅ stops navigation
                      e.stopPropagation();  // ⬅ stops bubbling
                      handleLogout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>
        )}
      </div>
      {showRequestCreditsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Request Credits</h2>

            <p style={{ marginBottom: "8px" }}>
              Enter the number of credits you would like to request.
            </p>

            <input
              type="number"
              min="100"
              step="1"
              value={requestedAmount}
              onChange={(e) => {
                const val = Number(e.target.value);
                setRequestedAmount(val);

                if (isNaN(val) || val < 100 || !Number.isInteger(val)) {
                  setRequestError("Minimum request is 100 credits (whole numbers only).");
                } else {
                  setRequestError("");
                }
              }}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                marginBottom: "10px"
              }}
            />

            {requestError && (
              <p style={{ color: "red", marginBottom: "10px" }}>{requestError}</p>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-save"
                disabled={requestError !== ""}
                onClick={submitCreditRequest}
              >
                Request
              </button>

              <button
                className="btn btn-cancel"
                onClick={() => setShowRequestCreditsModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
